import { useState, useEffect, useCallback } from 'react';
import { programsAPI, Program, ProgramCreate, ProgramType } from '@/services/api/programs';

interface UseProgramsReturn {
  programs: Program[];
  programTypes: ProgramType[];
  loading: boolean;
  error: string | null;
  createProgram: (data: ProgramCreate) => Promise<Program | null>;
  updateProgram: (id: number, data: Partial<ProgramCreate>) => Promise<Program | null>;
  deleteProgram: (id: number) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export default function usePrograms(): UseProgramsReturn {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programTypes, setProgramTypes] = useState<ProgramType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await programsAPI.listPrograms();
      setPrograms(data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch programs';
      setError(errorMessage);
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgramTypes = useCallback(async () => {
    try {
      const types = await programsAPI.getProgramTypes();
      setProgramTypes(types);
    } catch (err: any) {
      console.error('Error fetching program types:', err);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
    fetchProgramTypes();
  }, [fetchPrograms, fetchProgramTypes]);

  const createProgram = async (data: ProgramCreate): Promise<Program | null> => {
    setLoading(true);
    setError(null);

    try {
      const newProgram = await programsAPI.createProgram(data);
      setPrograms((prev) => [...prev, newProgram]);
      return newProgram;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create program';
      setError(errorMessage);
      console.error('Error creating program:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateProgram = async (id: number, data: Partial<ProgramCreate>): Promise<Program | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedProgram = await programsAPI.updateProgram(id, data);
      setPrograms((prev) => prev.map((p) => (p.id === id ? updatedProgram : p)));
      return updatedProgram;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update program';
      setError(errorMessage);
      console.error('Error updating program:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await programsAPI.deleteProgram(id);
      setPrograms((prev) => prev.filter((p) => p.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete program';
      setError(errorMessage);
      console.error('Error deleting program:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    programs,
    programTypes,
    loading,
    error,
    createProgram,
    updateProgram,
    deleteProgram,
    refetch: fetchPrograms,
  };
}
